import {CommonActivityStringProps, DEFAULT_TRUNCATED_WORDS_LIST_WIDTH_PERCENTAGE} from "../utils";
import {ActivityCarBusState} from "../../../../types-enums/activity-car-bus";
import {useTranslation} from "react-i18next";
import {Icon, TruncatedWordList} from "atmosfair-ui";
import React from "react";
import {
    generateCarBusRouteFromWaypointsString,
    generateProfessionalCarBusInformationString
} from "../../../../utils/string/car-bus";
import StringDisplayFootnoteIcon, {WHICH_FOOTNOTE_TYPE} from "../common/StringDisplayFootnoteIcon";
import CalculationTierTooltipHint from "../common/CalculationTierTooltipHint";
import {ACTIVITY_ICON_MAPPING} from "../../../../utils/getter/activities";
import {isEqual} from "lodash";
import {isCo2KgEValueDifferent} from "../../../../utils/check/common";

import {formatCo2ETotalString} from "../../../../utils/string/common";

interface CarBusRouteProps extends CommonActivityStringProps {
    carBus: ActivityCarBusState,
    hideDifferentDurationHint?: boolean,
    hideDifferentRouteHint?: boolean,
}

const CarBusInformation = ({
                               carBus,
                               truncatedWordsListMaxWidthPercentage = DEFAULT_TRUNCATED_WORDS_LIST_WIDTH_PERCENTAGE,
                               textColor,
                               iconColor,
                               hideDifferentDurationHint,
                               hideDifferentRouteHint,
                               whereToDisplay,
                               hideAllIconHints
                           }: CarBusRouteProps) => {
    const {
        waypoints,
        route: jsonRoute,
        originalRoute: originalJsonRoute,
        originalCountDays,
        countDays,
        vehicleType,
        common: {originalTotalEmissions, hideLoadBundleInformation, id},
        informationFromLoadedBundle,
        passengerCount,
    } = carBus
    const {i18n, t} = useTranslation()
    const {resolvedLanguage} = i18n
    const {carBusString, separator} = generateProfessionalCarBusInformationString(carBus, i18n, true, whereToDisplay)
    const detailedRoute = generateCarBusRouteFromWaypointsString(waypoints, i18n, false)
    const differentDuration = countDays !== originalCountDays
    const differentRoute = !isEqual(jsonRoute, originalJsonRoute)

    const _renderDifferentCo2KgEValuesOnLoadHint = () => {
        if (informationFromLoadedBundle !== null && originalTotalEmissions !== null) {
            const {previousEmissionsCo2KgE, countDays} = informationFromLoadedBundle
            const currentEmissionsCo2KgE = originalTotalEmissions[0].emissionKgCO2e
            if (
                currentEmissionsCo2KgE !== null &&
                previousEmissionsCo2KgE !== null &&
                isCo2KgEValueDifferent(currentEmissionsCo2KgE, previousEmissionsCo2KgE)
            ) {
                let previousEmissionsCo2KgEPerDay = previousEmissionsCo2KgE
                if (countDays !== null) previousEmissionsCo2KgEPerDay = previousEmissionsCo2KgE / countDays
                return <StringDisplayFootnoteIcon
                    tooltip={t("HINTS.EMISSION_CALCULATION.COMMON.DIFFERENT_CALCULATED_CO_KG_E", {
                        previousEmissionsCo2KgE: formatCo2ETotalString(previousEmissionsCo2KgEPerDay, resolvedLanguage),
                        currentEmissionsCo2KgE: formatCo2ETotalString(currentEmissionsCo2KgE, resolvedLanguage),
                    })}
                    iconColor={iconColor}
                    hideOnClick={true}
                    activityId={id}
                    whereToDisplay={whereToDisplay}
                    whichFootnoteType={WHICH_FOOTNOTE_TYPE.calculation}/>
            }
        }
    }

    return <div
        className={'activity-string-display-container'}
        style={{maxWidth: `${truncatedWordsListMaxWidthPercentage}%`}}>
        {vehicleType && <Icon
            containerClasses={"activity-string-display-activity-icon-margin"}
            size={"small"}
            color={iconColor}
            icon={ACTIVITY_ICON_MAPPING[vehicleType]}/>}
        <TruncatedWordList
            color={textColor}
            separator={separator}>
            {`${passengerCount} ${passengerCount === 1 ? t("GENERAL.PERSON") : t("GENERAL.PERSONS")}, ${carBusString}`}
        </TruncatedWordList>
        {
            detailedRoute &&
            <StringDisplayFootnoteIcon
                tooltip={detailedRoute}
                iconColor={iconColor}
                whereToDisplay={whereToDisplay}
                whichFootnoteType={WHICH_FOOTNOTE_TYPE.route}/>
        }
        {
            !hideDifferentDurationHint &&
            differentDuration &&
            <StringDisplayFootnoteIcon
                tooltip={t("HINTS.JOURNEY.CAR_BUS_REDUCED_TO_ONE_DAY")}
                iconColor={iconColor}
                whereToDisplay={whereToDisplay}
                whichFootnoteType={WHICH_FOOTNOTE_TYPE.date}/>
        }
        {
            !hideDifferentRouteHint &&
            differentRoute &&
            <StringDisplayFootnoteIcon
                tooltip={t("HINTS.JOURNEY.CAR_BUS_ROUTE_REMOVED")}
                iconColor={iconColor}
                whereToDisplay={whereToDisplay}
                whichFootnoteType={WHICH_FOOTNOTE_TYPE.warning}/>
        }
        {
            !hideAllIconHints &&
            !hideLoadBundleInformation &&
            _renderDifferentCo2KgEValuesOnLoadHint()
        }
        <CalculationTierTooltipHint iconColor={iconColor} activity={carBus}/>
    </div>
}

export default CarBusInformation